import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PaymentInfoForm from './PaymentInfoForm';
import ChatWidget from './ChatWidget';
import './CollaborationCreator.css';

// URL을 클릭 가능한 링크로 변환하고 줄바꿈을 처리하는 함수
const linkifyText = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a 
          key={index} 
          href={part} 
          target="_blank" 
          rel="noopener noreferrer"
          className="feedback-link"
        >
          {part}
        </a>
      );
    }
    // 줄바꿈을 <br> 태그로 변환
    return part.split('\n').map((line, lineIndex) => (
      <React.Fragment key={`${index}-${lineIndex}`}>
        {line}
        {lineIndex < part.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  });
};

interface CollaborationCreatorData {
  _id: string;
  responseId: string;
  email: string;
  handle: string;
  platform: string;
  agreedPrice: number;
  pricingType?: 'fixed' | 'rpm';
  rpmRate?: number;
  maxPrice?: number;
  calculatedPrice?: number;
  sourceType: string;
  collaborationStage: string;
  totalViews?: number;
  trolleyRecipientId?: string;
  scriptDraft?: string;
  scriptApproved?: boolean;
  scriptFeedback?: string;
  scriptSubmittedAt?: string;
  scriptApprovedAt?: string;
  scriptHistory?: Array<{
    type: 'submission' | 'feedback';
    version?: number;
    content: string;
    timestamp: string;
    approved?: boolean;
    author: 'Creator' | 'Admin';
  }>;
  videoFile?: string;
  videoApproved?: boolean;
  videoFeedback?: string;
  videoSubmittedAt?: string;
  videoApprovedAt?: string;
  videoHistory?: Array<{
    type: 'submission' | 'feedback';
    version?: number;
    content: string;
    timestamp: string;
    approved?: boolean;
    author: 'Creator' | 'Admin';
  }>;
  postUrlInstagram?: string;
  postUrlTikTok?: string;
  postUrlYoutube?: string;
  postVerified?: boolean;
  postSubmittedAt?: string;
  personalInfo?: {
    firstName?: string;
    lastName?: string;
    country?: string;
    address?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    submittedAt?: string;
  };
  invoiceLink?: string;
  taxInfo?: {
    type?: string;
    businessType?: string;
    taxId?: string;
    dateOfBirth?: string;
    foreignCountry?: string;
    submittedAt?: string;
  };

  // Stripe 쿠폰 정보
  stripeCoupon?: {
    couponId: string;
    couponCode: string;
    percentOff: number;
    redeemed: number;
    createdAt: string;
  };

  // Deadline management
  deadline?: string;
  lastReminderSent?: string;

  paymentDate?: string;
  paymentAmount?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Personal Info가 완전한지 확인하는 함수
const isPersonalInfoComplete = (personalInfo: any): boolean => {
  if (!personalInfo) return false;
  
  // 기본 필수 필드 확인
  const requiredFields = ['firstName', 'lastName', 'country', 'address', 'city', 'stateProvince', 'postalCode'];
  return requiredFields.every(field => personalInfo[field]);
};

const CollaborationCreator: React.FC = () => {
  const { collaborationId } = useParams<{ collaborationId: string }>();
  const navigate = useNavigate();
  
  const [workflow, setWorkflow] = useState<CollaborationCreatorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  
  // Form data for current step
  const [formData, setFormData] = useState({
    scriptDraft: '',
    videoDraft: null as File | null,
    videoMessage: '',
    postLink: '',
    tiktokUrl: '',
    instagramUrl: '',
    youtubeUrl: '',
    facebookUrl: ''
  });
  
  // Post guide acknowledgment states
  const [instagramGuideAcknowledged, setInstagramGuideAcknowledged] = useState(false);
  const [couponCodeIncluded, setCouponCodeIncluded] = useState(false);

  const statusSteps = {
    'awaiting_script': { step: 1, title: 'Script', description: 'Write your script draft', icon: '📝' },
    'reviewing_script': { step: 1, title: 'Script', description: 'Script under review', icon: '📄' },
    'awaiting_video': { step: 2, title: 'Video', description: 'Upload your video draft', icon: '🎥' },
    'reviewing_video': { step: 2, title: 'Video', description: 'Video under review', icon: '🎬' },
    'awaiting_post': { step: 3, title: 'Post', description: 'Share your post URL', icon: '📱' },
    'reviewing_post': { step: 3, title: 'Post', description: 'Post under review', icon: '🔍' },
    'awaiting_payment_info': { step: 4, title: 'Payment Info', description: 'Provide payment details', icon: '💳' },
    'processing_payment': { step: 4, title: 'Payment', description: 'Processing payment', icon: '⏳' },
    'completed': { step: 4, title: 'Completed', description: 'Payment completed!', icon: '✅' }
  };

  useEffect(() => {
    fetchCollaboration();
  }, [collaborationId]);

  const fetchCollaboration = async () => {
    try {
      // 크리에이터용 공개 API 사용 (인증 불필요)
      const response = await fetch(`${API_URL}/api/collaborations/public/${collaborationId}`);

      if (!response.ok) {
        throw new Error('Collaboration not found');
      }

      const data = await response.json();
      setWorkflow(data.data);
      
      // Set form data from existing workflow
      setFormData(prev => ({
        ...prev,
        scriptDraft: data.data.scriptDraft || '',
        postLink: ''
      }));
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collaboration');
    } finally {
      setLoading(false);
    }
  };



  // Payment Info Form 완료 핸들러
  const handlePaymentInfoComplete = async () => {
    await fetchCollaboration();
    setShowPaymentForm(false);
  };
  
  // 기존 Trolley Widget 핸들러 - 임시 유지
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleWidgetComplete = async (data: any) => {
    if (!workflow) return;
    
    setSubmitting(true);
    
    try {
      console.log('🎉 Trolley Widget completed:', data);
      
      // Widget 완료 데이터를 서버에 전송
      const response = await fetch(`${API_URL}/api/collaborations/${workflow._id}/payment-info`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          trolleyRecipientId: data.recipientId,
          trolleyWidgetData: data,
          paymentMethod: data.paymentMethod || 'paypal', // enum에 맞는 값 사용
          recipientType: data.recipientType || 'individual',
          country: data.country,
          currency: data.currency,
          completedAt: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.error || errorData?.message || 'Failed to process widget completion';
        throw new Error(errorMessage);
      }

      setError(null);
      toast.success('✅ Payment setup completed successfully!');
      await fetchCollaboration();
      setShowPaymentForm(false);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process payment setup';
      setError(errorMessage);
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleWidgetError = (error: any) => {
    console.error('❌ Trolley Widget error:', error);
    const errorMessage = error?.message || 'Widget error occurred';
    setError(errorMessage);
    toast.error(`❌ ${errorMessage}`);
    setSubmitting(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleWidgetProgress = (step: string) => {
    console.log('📋 Widget progress:', step);
    // 필요시 진행 상태 UI 업데이트
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workflow) return;

    setSubmitting(true);
    
    try {
      switch (workflow.collaborationStage) {
        case 'awaiting_script':
        case 'reviewing_script':  // Allow submission in reviewing state for revisions
          if (!formData.scriptDraft.trim()) {
            throw new Error('Script content is required');
          }
          
          const scriptResponse = await fetch(`${API_URL}/api/collaborations/public/${workflow._id}/script`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              scriptDraft: formData.scriptDraft
            })
          });

          if (!scriptResponse.ok) {
            throw new Error('Failed to submit script');
          }
          break;
          
        case 'awaiting_video':
        case 'reviewing_video':  // Allow submission in reviewing state for revisions
          if (!formData.videoDraft) {
            throw new Error('Video file is required');
          }
          
          const videoFormData = new FormData();
          videoFormData.append('videoFile', formData.videoDraft);
          if (formData.videoMessage && formData.videoMessage.trim()) {
            videoFormData.append('message', formData.videoMessage.trim());
          }

          const videoResponse = await fetch(`${API_URL}/api/collaborations/public/${workflow._id}/video`, {
            method: 'PATCH',
            body: videoFormData
          });

          if (!videoResponse.ok) {
            const errorData = await videoResponse.json().catch(() => null);
            const errorMessage = errorData?.error || errorData?.message || 
              (videoResponse.status === 413 ? 'Video file is too large. Maximum size is 1GB.' :
               videoResponse.status === 415 ? 'Invalid file format. Please upload a video file.' :
               videoResponse.status === 400 ? 'Invalid video file. Please check the file and try again.' :
               `Failed to upload video (Error ${videoResponse.status})`);
            throw new Error(errorMessage);
          }

          // 비디오 제출 성공 후 폼 초기화
          setFormData(prev => ({ ...prev, videoDraft: null, videoMessage: '' }));
          break;
          
        case 'awaiting_post':
          // Check platform-specific requirements
          const platform = workflow.platform.toLowerCase();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          let platformUrl = '';
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          let hasRequiredGuideAcknowledgment = true;
          
          if (platform === 'instagram') {
            if (!formData.instagramUrl) {
              throw new Error('Please provide Instagram URL');
            }
            platformUrl = formData.instagramUrl;
          } else if (platform === 'tiktok') {
            if (!formData.tiktokUrl) {
              throw new Error('Please provide TikTok URL');
            }
            platformUrl = formData.tiktokUrl;
          } else if (platform === 'youtube') {
            if (!formData.youtubeUrl) {
              throw new Error('Please provide YouTube URL');
            }
            platformUrl = formData.youtubeUrl;
          }
          
          // Send individual platform URLs to backend
          const postResponse = await fetch(`${API_URL}/api/collaborations/public/${workflow._id}/post`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              postUrlInstagram: platform === 'instagram' ? formData.instagramUrl || '' : '',
              postUrlTikTok: platform === 'tiktok' ? formData.tiktokUrl || '' : '',
              postUrlYoutube: platform === 'youtube' ? formData.youtubeUrl || '' : ''
            })
          });

          if (!postResponse.ok) {
            const errorData = await postResponse.json().catch(() => null);
            const errorMessage = errorData?.error || errorData?.message || 
              `Failed to submit post (Error ${postResponse.status})`;
            throw new Error(errorMessage);
          }
          break;
          
        default:
          throw new Error('Invalid workflow stage');
      }

      setError(null);
      toast.success('✅ Successfully submitted! We\'ll review it and get back to you soon.');
      await fetchCollaboration();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit';
      setError(errorMessage);
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };


  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
        <h3 className="loading-title">Loading your collaboration...</h3>
      </div>
    );
  }

  if (error && !workflow) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#ff4444' }}>
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')}>Go Back</button>
        </div>
      </div>
    );
  }

  if (!workflow) return null;

  const currentStep = statusSteps[workflow.collaborationStage as keyof typeof statusSteps] || statusSteps['awaiting_script'];
  const isCompleted = workflow.collaborationStage === 'completed';

  return (
    <div className="workflow-container">
      {/* Loading overlay when submitting */}
      {submitting && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <h3 className="loading-title">Processing your submission...</h3>
          <p className="loading-description">Please wait while we save your work</p>
        </div>
      )}

      <div className="workflow-card">
        {/* Header */}
        <div className="workflow-header">
          <h1 className="workflow-title">Collaboration Workflow</h1>
          <div className="collaboration-info">
            <div className="info-item">
              <span>📱</span>
              <span>{workflow.platform}</span>
            </div>
            <div className="info-item">
              <span className="info-icon">👤</span>
              <a
                href="#"
                className="info-label clickable-username"
                style={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'all 0.2s ease'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  const platform = workflow.platform.toLowerCase();
                  const handle = workflow.handle.replace('@', '');
                  let profileUrl = '';

                  if (platform === 'instagram') {
                    profileUrl = `https://www.instagram.com/${handle}`;
                  } else if (platform === 'tiktok') {
                    profileUrl = `https://www.tiktok.com/@${handle}`;
                  } else if (platform === 'youtube') {
                    profileUrl = `https://www.youtube.com/@${handle}`;
                  }

                  if (profileUrl) {
                    window.open(profileUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                  e.currentTarget.style.color = '#FF6B35';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                  e.currentTarget.style.color = 'inherit';
                }}
                title={`View ${workflow.handle}'s ${workflow.platform} profile`}
              >
                @{workflow.handle}
              </a>
            </div>
            <div className="info-item">
              <span>💰</span>
              <span>${workflow.agreedPrice}</span>
            </div>
            {workflow.stripeCoupon && (
              <div className="info-item">
                <span className="info-icon">🎫</span>
                <button
                  className="info-label coupon-button"
                  style={{
                    cursor: 'pointer',
                    fontWeight: '600',
                    color: '#FF6B35',
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    font: 'inherit',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(workflow.stripeCoupon!.couponCode);
                      toast.success(`🎫 Coupon code ${workflow.stripeCoupon!.couponCode} copied to clipboard!`);
                    } catch (err) {
                      // Fallback for browsers that don't support clipboard API
                      const textArea = document.createElement('textarea');
                      textArea.value = workflow.stripeCoupon!.couponCode;
                      textArea.style.position = 'fixed';
                      textArea.style.opacity = '0';
                      document.body.appendChild(textArea);
                      textArea.select();
                      // @ts-ignore: execCommand is deprecated but still needed for fallback
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      toast.success(`🎫 Coupon code ${workflow.stripeCoupon!.couponCode} copied!`);
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Click to copy coupon code"
                >
                  {workflow.stripeCoupon.couponCode}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Deadline Management */}
        {workflow.deadline && ['awaiting_script', 'awaiting_video', 'awaiting_post'].includes(workflow.collaborationStage) && (
          <DeadlineSection 
            workflow={workflow} 
            onWorkflowUpdate={setWorkflow} 
          />
        )}

        {/* Collaboration Guide Info */}
        <div className="collaboration-guide-card" style={{ 
          background: '#F5F5F5',
          padding: '25px 35px',
          margin: '0 0 30px 0',
          color: '#555',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #E0E0E0'
        }}>
          <h3 style={{ 
            marginBottom: '15px', 
            fontSize: '18px', 
            fontWeight: '600',
            color: '#333'
          }}>🎬 Collaboration Process Guide</h3>
          <p style={{ 
            marginBottom: '15px', 
            lineHeight: '1.6',
            color: '#555'
          }}>
            The collaboration consists of <strong>4 stages</strong>:
          </p>
          <ol style={{ 
            marginLeft: '20px', 
            marginBottom: '15px', 
            lineHeight: '1.8',
            color: '#555'
          }}>
            <li>
              <strong>Script Draft</strong> - Write and review the video script{' '}
              <a
                href="https://ssemble.notion.site/Script-Guide-a223bd01a8a046f2bb8ca6a4bcb9f425"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '11px',
                  padding: '2px 8px',
                  background: '#4a90e2',
                  color: 'white',
                  borderRadius: '3px',
                  textDecoration: 'none',
                  marginLeft: '8px',
                  fontWeight: '500'
                }}
              >
                Script Guide
              </a>
            </li>
            <li><strong>Video Draft</strong> - Create the video and receive feedback</li>
            <li><strong>Posting</strong> - Publish the completed video</li>
            <li><strong>Payment</strong> - Submit payment info and receive payment</li>
          </ol>
          
          {workflow.collaborationStage === 'awaiting_script' && (
            <div style={{ 
              background: 'rgba(255, 107, 53, 0.08)', 
              padding: '18px', 
              marginTop: '15px',
              border: '1px solid rgba(255, 107, 53, 0.2)'
            }}>
              <p style={{ 
                marginBottom: '10px', 
                fontWeight: 'bold',
                fontSize: '15px',
                color: '#444'
              }}>
                📝 Script Writing Guide
              </p>
              <p style={{ color: '#555', lineHeight: '1.5', marginBottom: '15px' }}>
                When writing your script, please refer to our{' '}
                <a
                  href="https://ssemble.notion.site/Script-Guide-a223bd01a8a046f2bb8ca6a4bcb9f425"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#FF6B35',
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#F77737';
                    e.currentTarget.style.textDecorationThickness = '2px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#FF6B35';
                    e.currentTarget.style.textDecorationThickness = '1px';
                  }}
                >
                  Script Guide
                </a>
                {' '}for planning and structuring your content.
              </p>
              {workflow.stripeCoupon && (
                <div style={{
                  background: '#F3E5F5',
                  border: '1px solid #BA68C8',
                  padding: '15px',
                  borderRadius: '6px',
                  marginTop: '15px'
                }}>
                  <p style={{
                    marginBottom: '10px',
                    fontWeight: 'bold',
                    color: '#6A1B9A',
                    fontSize: '14px'
                  }}>
                    🎫 Promote Your Discount Code
                  </p>
                  <p style={{ color: '#4A148C', fontSize: '13px', lineHeight: '1.5', marginBottom: '10px' }}>
                    Include this discount code in your content to help viewers save on their subscription:
                  </p>
                  <div style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #E1BEE7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px'
                  }}>
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#6A1B9A'
                    }}>
                      {workflow.stripeCoupon.couponCode}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(workflow.stripeCoupon!.couponCode);
                        toast.success(`🎫 Coupon code copied to clipboard!`);
                      }}
                      style={{
                        background: '#9C27B0',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#7B1FA2';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#9C27B0';
                      }}
                    >
                      Copy
                    </button>
                  </div>
                  <p style={{ color: '#4A148C', fontSize: '12px', lineHeight: '1.4', margin: '0' }}>
                    💡 <em>This gives viewers 10% off their first subscription and helps track the impact of your content!</em>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="progress-wrapper">
          <div className="progress-steps">
            <div className={`step ${
              ['awaiting_video', 'reviewing_video', 'awaiting_post', 'reviewing_post', 'awaiting_payment_info', 'processing_payment', 'completed'].includes(workflow.collaborationStage) ? 'completed' : 
              ['awaiting_script', 'reviewing_script'].includes(workflow.collaborationStage) ? 'active' : ''
            }`}>
              <div className="step-circle">📝</div>
              <span className="step-label">Script</span>
            </div>
            
            <div className={`step ${
              ['awaiting_post', 'reviewing_post', 'awaiting_payment_info', 'processing_payment', 'completed'].includes(workflow.collaborationStage) ? 'completed' : 
              ['awaiting_video', 'reviewing_video'].includes(workflow.collaborationStage) ? 'active' : ''
            }`}>
              <div className="step-circle">🎥</div>
              <span className="step-label">Video</span>
            </div>
            
            <div className={`step ${
              ['awaiting_payment_info', 'processing_payment', 'completed'].includes(workflow.collaborationStage) ? 'completed' : 
              ['awaiting_post', 'reviewing_post'].includes(workflow.collaborationStage) ? 'active' : ''
            }`}>
              <div className="step-circle">📱</div>
              <span className="step-label">Post</span>
            </div>
            
            <div className={`step ${
              workflow.collaborationStage === 'completed' ? 'completed' : 
              ['awaiting_payment_info', 'processing_payment'].includes(workflow.collaborationStage) ? 'active' : ''
            }`}>
              <div className="step-circle">💰</div>
              <span className="step-label">Payment</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="workflow-content">
          <h2 className="stage-title">{currentStep.icon} {currentStep.title}</h2>
          <p className="stage-description">{currentStep.description}</p>

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {/* Script Timeline */}
          {(workflow.collaborationStage === 'awaiting_script' || workflow.collaborationStage === 'reviewing_script') && (
            <>
              {workflow.scriptHistory && workflow.scriptHistory.length > 0 && (
                <div className="simple-timeline">
                  <h3 className="timeline-title">
                    <span>📝</span> Script Progress
                  </h3>
                  <div className="timeline-list">
                    {workflow.scriptHistory
                      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                      .map((item, index) => (
                      <div key={index} className="timeline-event">
                        <div className="event-marker">
                          {item.type === 'submission' ? '📄' : '💬'}
                        </div>
                        <div className="event-content">
                          {item.type === 'submission' ? (
                            <>
                              <div className="event-header">
                                <span className="event-title">Script v{item.version}</span>
                                <span className="event-time">{new Date(item.timestamp).toLocaleString()}</span>
                              </div>
                              <div className="event-text">{item.content}</div>
                            </>
                          ) : (
                            <>
                              <div className="event-header">
                                <span className={`event-title ${item.author === 'Admin' ? 'admin-feedback' : 'creator-feedback'}`}>
                                  {item.author === 'Admin' ? '👨‍💼 Admin' : '🎨 Creator'}
                                </span>
                                <span className="event-time">{new Date(item.timestamp).toLocaleString()}</span>
                                {item.approved && <span className="event-approved">✅</span>}
                              </div>
                              <div className="event-feedback">
                                {linkifyText(item.content)}
                                {(item as any).attachments && (item as any).attachments.length > 0 && (
                                  <div className="feedback-attachments-display">
                                    <p style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>Attachments:</p>
                                    <div className="attachment-grid">
                                      {(item as any).attachments.map((attachment: any, attIndex: number) => (
                                        <div key={attIndex} className="attachment-item">
                                          {attachment.mimetype.startsWith('image/') ? (
                                            <img
                                              src={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                              alt={attachment.originalname || attachment.filename}
                                              className="attachment-image"
                                            />
                                          ) : attachment.mimetype.startsWith('video/') ? (
                                            <video
                                              src={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                              controls
                                              preload="metadata"
                                              className="attachment-video"
                                              style={{
                                                display: 'block',
                                                outline: 'none'
                                              }}
                                            >
                                              Your browser does not support the video tag.
                                            </video>
                                          ) : (
                                            <a
                                              href={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="attachment-link"
                                            >
                                              📎 {attachment.filename}
                                            </a>
                                          )}
                                          <span className="attachment-name">{attachment.originalname || attachment.filename}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show form based on collaborationStage, not history */}
              {(() => {
                // If stage is reviewing_script, show waiting message
                if (workflow.collaborationStage === 'reviewing_script') {
                  return (
                    <div className="waiting-review">
                      <div className="waiting-icon">⏳</div>
                      <h3>Script Under Review</h3>
                      <p>Your script is being reviewed. We'll provide feedback soon.</p>
                    </div>
                  );
                }
                
                // If stage is awaiting_script, always show submission form
                if (workflow.collaborationStage === 'awaiting_script') {
                  const hasHistory = workflow.scriptHistory && workflow.scriptHistory.length > 0;
                  const isResubmission = hasHistory;
                  
                  return (
                    <form onSubmit={handleSubmit} className="timeline-form">
                      <div className="form-group">
                        <label className="form-label">
                          {isResubmission ? 'Submit Revised Script' : 'Script Draft'} <span className="form-required">*</span>
                        </label>
                        {isResubmission && (
                          <div className="form-help-text">
                            The stage has been reset. Please submit a new script.
                          </div>
                        )}
                        <textarea
                          className="form-textarea"
                          placeholder={isResubmission ? "Revise your script based on the feedback..." : "Write your script here..."}
                          value={formData.scriptDraft}
                          onChange={(e) => setFormData({ ...formData, scriptDraft: e.target.value })}
                          required
                        />
                      </div>
                      <button type="submit" className="btn-submit" disabled={submitting}>
                        {submitting ? 'Submitting...' : (isResubmission ? 'Submit Revised Script' : 'Submit Script')}
                      </button>
                    </form>
                  );
                }

                return null;
              })()}
            </>
          )}

          {/* Video Timeline */}
          {(workflow.collaborationStage === 'awaiting_video' || workflow.collaborationStage === 'reviewing_video') && (
            <>
              {workflow.videoHistory && workflow.videoHistory.length > 0 && (
                <div className="simple-timeline">
                  <h3 className="timeline-title">
                    <span>🎬</span> Video Progress
                  </h3>
                  <div className="timeline-list">
                    {workflow.videoHistory
                      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                      .map((item, index) => (
                      <div key={index} className="timeline-event">
                        <div className="event-marker">
                          {item.type === 'submission' ? '🎥' : '💬'}
                        </div>
                        <div className="event-content">
                          {item.type === 'submission' ? (
                            <>
                              <div className="event-header">
                                <span className="event-title">Video v{item.version}</span>
                                <span className="event-time">{new Date(item.timestamp).toLocaleString()}</span>
                              </div>
                              <video controls className="event-video">
                                <source src={item.content} type="video/mp4" />
                              </video>
                            </>
                          ) : (
                            <>
                              <div className="event-header">
                                <span className={`event-title ${item.author === 'Admin' ? 'admin-feedback' : 'creator-feedback'}`}>
                                  {item.author === 'Admin' ? '👨‍💼 Admin' : '🎨 Creator'}
                                </span>
                                <span className="event-time">{new Date(item.timestamp).toLocaleString()}</span>
                                {item.approved && <span className="event-approved">✅</span>}
                              </div>
                              <div className="event-feedback">
                                {linkifyText(item.content)}
                                {(item as any).attachments && (item as any).attachments.length > 0 && (
                                  <div className="feedback-attachments-display">
                                    <p style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>Attachments:</p>
                                    <div className="attachment-grid">
                                      {(item as any).attachments.map((attachment: any, attIndex: number) => (
                                        <div key={attIndex} className="attachment-item">
                                          {attachment.mimetype.startsWith('image/') ? (
                                            <img
                                              src={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                              alt={attachment.originalname || attachment.filename}
                                              className="attachment-image"
                                            />
                                          ) : attachment.mimetype.startsWith('video/') ? (
                                            <video
                                              src={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                              controls
                                              preload="metadata"
                                              className="attachment-video"
                                              style={{
                                                display: 'block',
                                                outline: 'none'
                                              }}
                                            >
                                              Your browser does not support the video tag.
                                            </video>
                                          ) : (
                                            <a
                                              href={attachment.path.startsWith('http') ? attachment.path : `${API_URL}/api/collaborations/feedback-attachment/${attachment.filename || attachment.path.split('/').pop()}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="attachment-link"
                                            >
                                              📎 {attachment.filename}
                                            </a>
                                          )}
                                          <span className="attachment-name">{attachment.originalname || attachment.filename}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show form based on collaborationStage, not history */}
              {(() => {
                // If stage is reviewing_video, show waiting message
                if (workflow.collaborationStage === 'reviewing_video') {
                  return (
                    <div className="waiting-review">
                      <div className="waiting-icon">⏳</div>
                      <h3>Video Under Review</h3>
                      <p>Your video is being reviewed. We'll provide feedback soon.</p>
                    </div>
                  );
                }
                
                // If stage is awaiting_video, always show submission form
                if (workflow.collaborationStage === 'awaiting_video') {
                  const hasHistory = workflow.videoHistory && workflow.videoHistory.length > 0;
                  const isResubmission = hasHistory;
                  
                  return (
                    <form onSubmit={handleSubmit} className="timeline-form">
                      <div className="form-group">
                        <label className="form-label">
                          {isResubmission ? 'Upload Revised Video' : 'Video File'} <span className="form-required">*</span>
                        </label>
                        {isResubmission && (
                          <div className="form-help-text">
                            The stage has been reset. Please upload a new video.
                          </div>
                        )}
                        <div 
                          className="file-upload-area"
                          onClick={() => document.getElementById('video-input-main')?.click()}
                        >
                          {formData.videoDraft ? (
                            <div className="selected-file">
                              <span className="file-icon">🎥</span>
                              <div className="file-info">
                                <div className="file-name">{formData.videoDraft.name}</div>
                                <div className="file-size">
                                  {(formData.videoDraft.size / (1024 * 1024)).toFixed(2)} MB
                                </div>
                              </div>
                              <button
                                type="button"
                                className="file-remove"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormData({ ...formData, videoDraft: null });
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="file-upload-icon">📹</div>
                              <div className="file-upload-text">
                                {isResubmission ? 'Click to upload revised video' : 'Click to upload video'}
                              </div>
                              <div className="file-upload-hint">MP4, MOV up to 2GB</div>
                            </>
                          )}
                          <input
                            id="video-input-main"
                            type="file"
                            className="file-input"
                            accept="video/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setFormData({ ...formData, videoDraft: file });
                              }
                            }}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Message (Optional)
                        </label>
                        <textarea
                          className="form-textarea"
                          placeholder="Add any notes or comments about your video submission..."
                          value={formData.videoMessage}
                          onChange={(e) => setFormData({ ...formData, videoMessage: e.target.value })}
                          rows={4}
                        />
                      </div>

                      <button type="submit" className="btn-submit" disabled={submitting}>
                        {submitting ? 'Uploading...' : (isResubmission ? 'Submit Revised Video' : 'Submit Video')}
                      </button>
                    </form>
                  );
                }

                return null;
              })()}
            </>
          )}

          {/* Post stage forms */}

          {workflow.collaborationStage === 'awaiting_post' && (
            <form onSubmit={handleSubmit}>

              {/* Coupon Code Reminder - Only show if coupon exists */}
              {workflow.stripeCoupon && (
                <div className="platform-guide-card" style={{
                  background: '#F3E5F5',
                  border: '2px solid #BA68C8',
                  padding: '20px',
                  marginBottom: '25px',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ color: '#6A1B9A', marginBottom: '15px', fontSize: '17px', fontWeight: 'bold' }}>
                    🎫 Important: Include the discount code in the first line of your post description.
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#333' }}>
                    <p style={{ marginBottom: '15px' }}>
                      Please add the discount code at the very first line of your post description to help your viewers save on their subscription:
                    </p>
                    <div style={{
                      background: 'white',
                      padding: '15px',
                      borderRadius: '6px',
                      border: '1px solid #E1BEE7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '15px'
                    }}>
                      <span style={{
                        fontFamily: 'monospace',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#6A1B9A',
                        letterSpacing: '2px'
                      }}>
                        {workflow.stripeCoupon.couponCode}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(workflow.stripeCoupon!.couponCode);
                          toast.success(`🎫 Coupon code copied to clipboard!`);
                        }}
                        style={{
                          background: '#9C27B0',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#7B1FA2';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#9C27B0';
                        }}
                      >
                        Copy Code
                      </button>
                    </div>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                      💡 This code gives your viewers <strong>10% off</strong> their first subscription and helps track the impact of your content!
                    </p>

                    <div style={{
                      background: '#FCE4EC',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #F8BBD0'
                    }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '14px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        color: '#880E4F'
                      }}>
                        <input
                          type="checkbox"
                          checked={couponCodeIncluded}
                          onChange={(e) => setCouponCodeIncluded(e.target.checked)}
                          style={{
                            marginRight: '10px',
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                        I have included the discount code "{workflow.stripeCoupon.couponCode}" in my post description
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Platform-specific content */}
              {workflow.platform.toLowerCase() === 'instagram' && (
                <div className="form-group">
                  <label className="form-label">
                    Instagram URL <span className="form-required">*</span>
                  </label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://www.instagram.com/p/..."
                    value={formData.instagramUrl || ''}
                    onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })}
                    required
                  />
                </div>
              )}

              {workflow.platform.toLowerCase() === 'tiktok' && (
                <div className="form-group">
                  <label className="form-label">
                    TikTok URL <span className="form-required">*</span>
                  </label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://www.tiktok.com/@username/video/..."
                    value={formData.tiktokUrl || ''}
                    onChange={(e) => setFormData({ ...formData, tiktokUrl: e.target.value })}
                    required
                  />
                </div>
              )}

              {workflow.platform.toLowerCase() === 'youtube' && (
                <div className="form-group">
                  <label className="form-label">
                    YouTube URL <span className="form-required">*</span>
                  </label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={formData.youtubeUrl || ''}
                    onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                    required
                  />
                </div>
              )}

              <div className="button-group">
                <button 
                  type="submit" 
                  className="btn-submit" 
                  disabled={
                    submitting ||
                    (workflow.stripeCoupon && !couponCodeIncluded) ||
                    (workflow.platform.toLowerCase() === 'instagram' && !formData.instagramUrl) ||
                    (workflow.platform.toLowerCase() === 'tiktok' && !formData.tiktokUrl) ||
                    (workflow.platform.toLowerCase() === 'youtube' && !formData.youtubeUrl)
                  }
                >
                  {submitting ? 'Submitting...' : 'Submit Post URL'}
                </button>
              </div>
            </form>
          )}

          {/* Post review stage */}
          {workflow.collaborationStage === 'reviewing_post' && (
            <div className="waiting-review">
              <div className="waiting-icon">🔍</div>
              <h3>Post Under Review</h3>
              <p>Your post has been submitted and is being reviewed.</p>
              <p>We're verifying that the content matches the approved script.</p>
              {(workflow.postUrlInstagram || workflow.postUrlTikTok || workflow.postUrlYoutube) && (
                <div className="submitted-post-info">
                  <strong>Submitted URLs:</strong>
                  <div className="submitted-urls">
                    {workflow.postUrlInstagram && (
                      <div className="submitted-url-item">
                        <strong>Instagram:</strong> <a href={workflow.postUrlInstagram} target="_blank" rel="noopener noreferrer">{workflow.postUrlInstagram}</a>
                      </div>
                    )}
                    {workflow.postUrlTikTok && (
                      <div className="submitted-url-item">
                        <strong>TikTok:</strong> <a href={workflow.postUrlTikTok} target="_blank" rel="noopener noreferrer">{workflow.postUrlTikTok}</a>
                      </div>
                    )}
                    {workflow.postUrlYoutube && (
                      <div className="submitted-url-item">
                        <strong>YouTube:</strong> <a href={workflow.postUrlYoutube} target="_blank" rel="noopener noreferrer">{workflow.postUrlYoutube}</a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {workflow.collaborationStage === 'awaiting_payment_info' && (
            <>
              {isPersonalInfoComplete(workflow.personalInfo) &&
               workflow.invoiceLink &&
               (workflow.agreedPrice < 600 || workflow.taxInfo?.taxId) &&
               !showPaymentForm ? (
                <div className="payment-info-summary">
                  <div className="info-submitted">
                    <div className="info-icon">✅</div>
                    <h3>Payment Information Submitted</h3>
                    <p>Your payment information has been received and is being reviewed.</p>
                    <div className="payment-details-summary">
                      <p><strong>Country:</strong> {workflow.personalInfo?.country}</p>
                      {workflow.pricingType === 'rpm' ? (
                        <>
                          <p><strong>Pricing Type:</strong> RPM (Performance-based)</p>
                          <p><strong>Total Views:</strong> {workflow.totalViews?.toLocaleString() || 'Calculating...'}</p>
                          <p><strong>RPM Rate:</strong> ${workflow.rpmRate} per 1000 views</p>
                          <p><strong>Calculated Amount:</strong> ${workflow.calculatedPrice?.toFixed(2) || 'Pending'}</p>
                          <p><strong>Max Amount:</strong> ${workflow.maxPrice}</p>
                        </>
                      ) : (
                        <p><strong>Payment Amount:</strong> ${workflow.agreedPrice}</p>
                      )}
                      <p><strong>Submitted:</strong> {new Date(workflow.personalInfo?.submittedAt || '').toLocaleString()}</p>
                    </div>
                    <button
                      className="btn-update"
                      onClick={() => setShowPaymentForm(true)}
                    >
                      Update Payment Information
                    </button>
                  </div>
                </div>
              ) : (
                <PaymentInfoForm
                  collaborationId={workflow._id}
                  email={workflow.email}
                  agreedPrice={workflow.agreedPrice}
                  handle={workflow.handle}
                  platform={workflow.platform}
                  existingPaymentInfo={{
                    ...workflow.personalInfo,
                    taxInfo: workflow.taxInfo
                  }}
                  onComplete={handlePaymentInfoComplete}
                />
              )}
            </>
          )}

          {workflow.collaborationStage === 'processing_payment' && (
            <div className="success-message">
              <div className="success-icon">⏳</div>
              <h3 className="success-title">Payment Processing</h3>
              <p className="success-description">
                Great! We've received your payment details. Your ${
                  workflow.pricingType === 'rpm'
                    ? (workflow.calculatedPrice?.toFixed(2) || workflow.maxPrice)
                    : workflow.agreedPrice
                } payment is being processed.
              </p>
              {workflow.pricingType === 'rpm' && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#f0f9ff', borderRadius: '8px' }}>
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>
                    📊 <strong>Performance-based Payment:</strong>
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>
                    {workflow.totalViews?.toLocaleString() || 'N/A'} views × ${workflow.rpmRate}/1000 = ${workflow.calculatedPrice?.toFixed(2) || 'Calculating'}
                  </p>
                </div>
              )}
              <div className="payment-method-info">
                <p>You will receive payment via: <strong>PayPal</strong></p>
                <p className="payment-timeline">
                  <strong>⏰ Payment will be processed within 24 hours.</strong>
                </p>
              </div>
            </div>
          )}

          {isCompleted && (
            <div className="success-message">
              <div className="success-icon">🎉</div>
              <h3 className="success-title">Collaboration Completed!</h3>
              <p className="success-description">
                Your ${
                  workflow.paymentAmount ||
                  (workflow.pricingType === 'rpm' ? workflow.calculatedPrice : workflow.agreedPrice)
                } payment has been successfully sent.
                Thank you for your amazing collaboration!
              </p>
            </div>
          )}
        </div>
      </div>
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Chat Widget */}
      <ChatWidget
        collaborationId={workflow._id}
        userType="creator"
      />
    </div>
  );
};

// Deadline Management Component
interface DeadlineSectionProps {
  workflow: CollaborationCreatorData;
  onWorkflowUpdate: (workflow: CollaborationCreatorData) => void;
}

const DeadlineSection: React.FC<DeadlineSectionProps> = ({ workflow, onWorkflowUpdate }) => {
  const [newDate, setNewDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  useEffect(() => {
    if (workflow.deadline) {
      // Format date for input field (YYYY-MM-DD)
      const deadlineDate = new Date(workflow.deadline);
      const formattedDate = deadlineDate.toISOString().split('T')[0];
      setNewDate(formattedDate);
    }
  }, [workflow.deadline]);
  
  const isOverdue = workflow.deadline && new Date() > new Date(workflow.deadline);
  
  const handleDateUpdate = async () => {
    if (!newDate) return;
    
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/collaborations/${workflow._id}/deadline`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deadline: newDate })
      });
      
      const result = await response.json();
      if (result.success) {
        onWorkflowUpdate(result.data);
        toast.success('Deadline updated successfully!');
      } else {
        toast.error('Failed to update deadline: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating deadline:', error);
      toast.error('Failed to update deadline: ' + (error as Error).message);
    } finally {
      setIsUpdating(false);
    }
  };
  
  const stageNames = {
    'awaiting_script': 'Script Writing',
    'awaiting_video': 'Video Creation',
    'awaiting_post': 'Content Publishing'
  } as const;
  
  const stageName = stageNames[workflow.collaborationStage as keyof typeof stageNames];
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  return (
    <div style={{ 
      background: isOverdue ? '#fff5f5' : '#f8f9fa', 
      border: isOverdue ? '2px solid #fc8181' : '1px solid #e2e8f0',
      padding: '20px', 
      borderRadius: '8px',
      marginBottom: '20px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
        <h3 style={{ margin: 0, color: isOverdue ? '#e53e3e' : '#2d3748' }}>
          📅 {stageName} Deadline
        </h3>
      </div>
      
      {isOverdue && (
        <div style={{ 
          color: '#e53e3e', 
          background: '#fed7d7',
          padding: '10px',
          borderRadius: '6px',
          marginBottom: '15px' 
        }}>
          <strong>⚠️ Deadline has passed!</strong> Please set a new completion date below.
        </div>
      )}
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'auto 1fr auto', 
        gap: '10px', 
        alignItems: 'center' 
      }}>
        <label style={{ fontWeight: 'bold', color: '#4a5568' }}>
          Completion Date:
        </label>
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
          style={{ 
            padding: '8px 12px', 
            borderRadius: '6px', 
            border: '1px solid #d1d5db',
            fontSize: '14px'
          }}
          disabled={isUpdating}
        />
        <button
          onClick={handleDateUpdate}
          disabled={isUpdating || !newDate}
          style={{
            background: '#ff4000',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            fontWeight: 'bold',
            cursor: isUpdating || !newDate ? 'not-allowed' : 'pointer',
            opacity: isUpdating || !newDate ? 0.6 : 1,
            minWidth: '80px'
          }}
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </button>
      </div>
      
      <div style={{ 
        background: '#e6fffa', 
        padding: '12px', 
        borderRadius: '6px', 
        marginTop: '15px',
        fontSize: '14px',
        color: '#234e52'
      }}>
        <strong>💡 Flexible Scheduling:</strong> You can adjust your deadline anytime to fit your schedule. 
        No approval needed - just set a date that works for you!
      </div>
      
      {workflow.deadline && (
        <div style={{ 
          fontSize: '12px', 
          color: '#6b7280', 
          marginTop: '8px' 
        }}>
          Current deadline: {formatDate(workflow.deadline)}
        </div>
      )}
    </div>
  );
};

export default CollaborationCreator;